/**
 * N2 · Competência 6 (Estatística) · task-model H1.37.K01
 *
 *   "Avaliar uma conclusão publicada a partir de um gráfico cujo eixo vertical não
 *    começa em zero."
 *
 * O PRIMEIRO ITEM DO EIXO COGNITIVO III com manipulação. A matriz tem 29 habilidades
 * de "avaliar, analisar, modificar" e o banco só as cobria pelos itens de encontrar o
 * erro. Aqui o aluno não refaz a conta de outra pessoa: ele julga uma AFIRMAÇÃO
 * publicada, e o instrumento do julgamento é a própria figura.
 *
 * POR QUE ELE SÓ EXISTE NO DIGITAL. No papel, o gráfico com o eixo cortado é uma
 * imagem fixa, e o aluno precisa imaginar como ele ficaria a partir do zero. Aqui o
 * seletor move o início do eixo, e a "diferença gritante" encolhe na frente dele até
 * virar o que sempre foi: uma diferença de um décimo. A manipulação é ferramenta de
 * pensamento — o item continua sendo respondido por escolha —, e é por isso que ele é
 * N2 e não N3.
 *
 * A ESTRUTURA É E7: as alternativas são JULGAMENTOS, não valores. Desde a v2 são só
 * DOIS, a pedido do autor, na redação dele: "sustenta-se, pois a altura da coluna de A
 * é o dobro da de B" — o erro de ler as alturas desenhadas — e "não se sustenta, pois
 * é 1,25 vezes" — a chave. As outras três alternativas da v1 (tomar a diferença por
 * razão, a coluna mais alta é outra, faltam dados…) eram enchimento que ninguém
 * marcaria, e diluíam a única escolha que o item quer ver.
 */

import { formatBR } from '../../core/num.ts';
import { conta, de, oS, type NomeId } from '../../core/pt.ts';
import type { Rng } from '../../core/rng.ts';
import { assembleSelectOne } from '../../engine/assemble.ts';
import { comExplicacoes, type Explicacoes } from '../../engine/explain.ts';
import { barras } from '../../render/chart.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import { GRAF_EIXO_TRUNCADO } from '../../schema/errors.ts';
import type { BuggySolver, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

/** Os dois julgamentos: a chave é `justa`; `alturas` é o erro de ler o desenho. */
type Veredito = 'justa' | 'alturas';

interface Contexto {
  readonly abertura: string;
  readonly titulo: string;
  readonly eixo: string;
  readonly unidade: string;
  /** Quatro categorias; a manchete compara a segunda com a quarta. */
  readonly categorias: readonly [string, string, string, string];
  readonly veiculo: string;
  /**
   * Como o retorno se refere à afirmação depois de citá-la. Era "a manchete" fixo — e
   * numa das quatro variantes o que se julga é o texto de uma propaganda, e noutra a
   * conclusão de um relatório.
   */
  readonly afirmacao: NomeId;
  /** A manchete, no verbo do contexto: "vende", "recebe", "dura". */
  readonly manchete: (maior: string, menor: string, vezes: string) => string;
  /** "a coluna associada à Fábrica 1", "ao Modelo A": a regência de cada categoria. */
  readonly associada: (categoria: string) => string;
}

const CONTEXTOS: readonly Contexto[] = [
  {
    abertura:
      'Uma revista publicou o gráfico abaixo com as vendas de quatro lojas de uma rede, em milhares de reais.',
    titulo: 'Vendas por loja',
    eixo: 'Vendas (mil R$)',
    unidade: 'mil reais',
    categorias: ['Centro', 'Norte', 'Sul', 'Leste'],
    veiculo: 'A manchete da revista',
    afirmacao: 'manchete',
    manchete: (a, b, n) => `${a} vende ${n} vezes mais que ${b}`,
    associada: (c) => `à loja ${c}`,
  },
  {
    abertura:
      'Um jornal publicou o gráfico abaixo com o número de visitantes de quatro museus da cidade, em milhares.',
    titulo: 'Visitantes por museu',
    eixo: 'Visitantes (mil)',
    unidade: 'mil visitantes',
    categorias: ['Arte', 'Ciência', 'História', 'Imagem'],
    veiculo: 'A manchete do jornal',
    afirmacao: 'manchete',
    manchete: (a, b, n) => `Museu ${a} recebe ${n} vezes mais visitantes que o Museu ${b}`,
    associada: (c) => `ao Museu ${c}`,
  },
  {
    abertura:
      'Uma propaganda mostrou o gráfico abaixo com a duração da bateria de quatro modelos de celular, em horas.',
    titulo: 'Duração da bateria',
    eixo: 'Duração (h)',
    unidade: 'horas',
    categorias: ['Modelo A', 'Modelo B', 'Modelo C', 'Modelo D'],
    veiculo: 'O texto da propaganda',
    afirmacao: 'texto',
    manchete: (a, b, n) => `A bateria do ${a} dura ${n} vezes mais que a do ${b}`,
    associada: (c) => `ao ${c}`,
  },
  {
    abertura:
      'Um relatório publicou o gráfico abaixo com a produção mensal de quatro fábricas, em milhares de peças.',
    titulo: 'Produção por fábrica',
    eixo: 'Produção (mil peças)',
    unidade: 'mil peças',
    categorias: ['Fábrica 1', 'Fábrica 2', 'Fábrica 3', 'Fábrica 4'],
    veiculo: 'A conclusão do relatório',
    afirmacao: 'conclusao',
    manchete: (a, b, n) => `A ${a} produz ${n} vezes mais que a ${b}`,
    associada: (c) => `à ${c}`,
  },
];

export interface Params {
  readonly contexto: number;
  /** Os quatro valores, na ordem das categorias. */
  readonly valores: readonly [number, number, number, number];
  /** Índices do menor e do maior — os dois que a manchete compara. */
  readonly iMenor: number;
  readonly iMaior: number;
  /** Onde o eixo começa no gráfico publicado. */
  readonly corte: number;
  readonly yTop: number;
  /** Quantas vezes a coluna maior PARECE ser a menor, no gráfico publicado. */
  readonly razaoAparente: number;
}

const PASSO = 5;

/**
 * Amostragem RESPOSTA PRIMEIRO, de novo: o que precisa sair redondo é a razão
 * APARENTE — ela é a manchete, e uma manchete com "1,87 vez" não engana ninguém.
 * Escolhe-se primeiro essa razão (2 ou 3), depois a folga acima do corte, e os valores
 * saem daí. A razão verdadeira fica perto de 1, que é o ponto do item.
 */
function drawParams(rng: Rng): Params {
  const razaoAparente = rng.pick([2, 3]);
  const menor = rng.pick([60, 70, 80, 90, 100, 110]);
  // Folga da coluna menor acima do corte; a da maior é `folga * razaoAparente`. Nunca
  // menor que 10 — com 5 não sobra múltiplo de 5 entre as duas colunas comparadas, e o
  // gráfico saía com colunas empatadas. E nunca tão grande que a razão VERDADEIRA passe
  // de 1,45: se as duas já fossem de fato muito diferentes, a manchete não estaria
  // enganando ninguém. Escolher a folga aqui é melhor que rejeitar depois — rejeitava
  // duas variantes em cada cinco.
  const folga = rng.pick([10, 15, 20].filter((g) => (g * (razaoAparente - 1)) / menor <= 0.45));
  const corte = menor - folga;
  const maior = corte + folga * razaoAparente;

  // As outras duas colunas: múltiplos do passo acima do corte, distintas entre si e
  // das duas comparadas. Elas dão realidade ao gráfico — sem elas, o item seria duas
  // barras no vazio.
  const acima: number[] = [];
  const abaixo: number[] = [];
  for (let v = corte + PASSO; v <= maior + folga; v += PASSO) {
    if (v === menor || v === maior) continue;
    (v > maior ? acima : abaixo).push(v);
  }
  // Uma delas passa do maior: assim a coluna mais alta do gráfico nunca é uma das duas
  // que a manchete compara, e o distrator "a mais alta é outra" diz algo verdadeiro
  // sobre outra pergunta — em vez de algo que a figura desmente na hora.
  const outras = [rng.pick(acima) as number, rng.pick(abaixo) as number];
  const valores = rng.shuffle([menor, maior, ...outras]) as [number, number, number, number];

  return {
    contexto: rng.int(0, CONTEXTOS.length - 1),
    valores,
    iMenor: valores.indexOf(menor),
    iMaior: valores.indexOf(maior),
    corte,
    yTop: Math.ceil((Math.max(...valores) + folga / 2) / 10) * 10,
    razaoAparente,
  };
}

const nomes = (p: Params): { menor: string; maior: string; topo: string } => {
  const c = CONTEXTOS[p.contexto] as Contexto;
  const iTopo = p.valores.indexOf(Math.max(...p.valores));
  return {
    menor: c.categorias[p.iMenor] as string,
    maior: c.categorias[p.iMaior] as string,
    topo: c.categorias[iTopo] as string,
  };
};

const val = (p: Params, i: number): number => p.valores[i] as number;
const razaoReal = (p: Params): number => val(p, p.iMaior) / val(p, p.iMenor);
const diferenca = (p: Params): number => val(p, p.iMaior) - val(p, p.iMenor);

const n0 = (x: number): string => formatBR(x, 0);
const afirmacaoDe = (p: Params): NomeId => (CONTEXTOS[p.contexto] as Contexto).afirmacao;

/** A razão verdadeira tem duas casas exatas? (100 ÷ 80 = 1,25; 115 ÷ 75 não tem.) */
const razaoExata = (p: Params): boolean => {
  const r = razaoReal(p);
  return Math.abs(r * 100 - Math.round(r * 100)) < 1e-9;
};

/** A razão verdadeira com duas casas: "1,25", e "1,53" quando arredondada. */
const razaoTexto = (p: Params): string => formatBR(Math.round(razaoReal(p) * 100) / 100);

/**
 * A conta da razão, com o sinal que ela merece.
 *
 * Escrever "70 ÷ 60 = 1,2" é afirmar ao aluno uma igualdade falsa num item que trata
 * justamente de leitura honesta de gráfico. O sinal vira `≈` quando as duas casas
 * escritas não dão conta do valor. E são duas casas desde a v2: com uma, 100 ÷ 80
 * saía "≈ 1,3", quando a razão é exatamente 1,25 — o autor apontou.
 */
const contaDaRazao = (p: Params): string =>
  `${n0(val(p, p.iMaior))} ÷ ${n0(val(p, p.iMenor))} ${razaoExata(p) ? '=' : '≈'} ${conta(razaoTexto(p), 'vez')}`;

/** A razão aparente, como se diz: "o dobro", "o triplo". */
const vezesAparente = (p: Params): string =>
  ['', '', 'o dobro', 'o triplo'][p.razaoAparente] ?? `${n0(p.razaoAparente)} vezes`;

/**
 * Os dois julgamentos, na redação do autor. Paralelos de propósito: o comprimento não
 * pode denunciar a chave.
 */
function julgamento(p: Params, v: Veredito): string {
  const ctx = CONTEXTOS[p.contexto] as Contexto;
  const { menor, maior } = nomes(p);
  const colunaDe = (c: string): string => `a altura da coluna associada ${ctx.associada(c)}`;
  switch (v) {
    case 'justa':
      return `Não se sustenta, pois ${colunaDe(maior)} é ${razaoExata(p) ? '' : 'cerca de '}${conta(razaoTexto(p), 'vez')} ${colunaDe(menor)}.`;
    case 'alturas':
      return `Sustenta-se, pois ${colunaDe(maior)} é ${vezesAparente(p)} ${de(colunaDe(menor))}.`;
  }
}

const BUGGY: readonly BuggySolver<Params, Veredito>[] = [
  // O erro que dá nome ao task-model: comparar as alturas desenhadas.
  { error: GRAF_EIXO_TRUNCADO, priority: 1, solve: () => 'alturas' },
];

const EXPLICA_BASE: Explicacoes<Params, Veredito> = {
  [GRAF_EIXO_TRUNCADO]: (p) => {
    const { menor, maior } = nomes(p);
    return [
      txt(
        `No gráfico publicado o eixo começa em ${n0(p.corte)}, e não em zero: sobra ` +
          `${n0(val(p, p.iMaior) - p.corte)} de coluna para ${maior} contra ` +
          `${n0(val(p, p.iMenor) - p.corte)} para ${menor}, o que dá ${vezesAparente(p)} que ${oS(afirmacaoDe(p))} anuncia. `,
      ),
      txt(
        `Mas as colunas inteiras medem ${n0(val(p, p.iMaior))} e ${n0(val(p, p.iMenor))}: ` +
          `${contaDaRazao(p)}. O corte na base é que exagera.`,
      ),
    ];
  },
};

/**
 * Toda explicação começa repetindo o julgamento que o aluno marcou.
 *
 * Nos itens numéricos essa amarração é natural — a explicação termina no número da
 * alternativa. Aqui a alternativa é uma frase, e a bateria confere a mesma coisa: o
 * comentário tem de citar o que o aluno escolheu, senão parece estar falando de outra
 * resposta.
 */
const EXPLICA: Explicacoes<Params, Veredito> = Object.fromEntries(
  Object.entries(EXPLICA_BASE).map(([codigo, escrever]) => [
    codigo,
    (p: Params, v: Veredito): RichText => [
      txt(`Você marcou: “${julgamento(p, v)}” `),
      ...escrever(p, v),
    ],
  ]),
);

export const eixoTruncado: ItemTemplate<Params, Veredito> = {
  id: templateId('h1-37-K01-eixo-truncado'),
  // v2: só dois julgamentos, na redação do autor, e a razão verdadeira com duas casas.
  version: 2,
  alternativas: 2,
  taskModel: taskModelCode('H1.37.K01'),
  skills: [skillCode('H1.37')],
  axis: 'III',
  level: 'N2',
  title: 'Manchete e eixo que não começa em zero',
  responseModel: '3PL',

  difficultySources: ['two-supports', 'steps.2'],

  params: drawParams,
  solve: () => 'justa',
  buggy: comExplicacoes(BUGGY, EXPLICA),

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    // Razão aparente de 3 convence mais que a de 2 — e é mais difícil de desmontar.
    if (p.razaoAparente >= 3) fontes.push('distractor-proximity.high');
    if (val(p, p.iMenor) >= 100) fontes.push('non-round-numbers');
    return fontes;
  },

  build(p, key, distractors, rng) {
    const ctx = CONTEXTOS[p.contexto] as Contexto;
    const { menor, maior } = nomes(p);

    const descricao = (inicio: number): string =>
      `Gráfico de colunas com ${ctx.titulo.toLowerCase()}: ` +
      ctx.categorias.map((c, i) => `${c}, ${n0(val(p, i))}`).join('; ') +
      `. O eixo vertical começa em ${n0(inicio)}` +
      (inicio > 0 ? ', com a marca de interrupção na base.' : ' — do zero.');

    const mancheteDoContexto = ctx.manchete(maior, menor, n0(p.razaoAparente));

    const stem: RichText = [
      txt(`${ctx.abertura} `),
      txt(`${ctx.veiculo} diz: `),
      strong(`“${mancheteDoContexto}”`),
      txt('.'),
      br,
      // Molde do autor (2026-09-29) para os itens com explorador ou controle.
      txt('Utilize o controle abaixo para mudar o valor em que o eixo vertical '),
      strong('começa'),
      txt('. Observe o que acontece com as colunas quando ele chega a zero.'),
    ];

    const interaction = assembleSelectOne<Veredito>({
      structure: 'E7',
      key,
      distractors,
      render: (v) => [txt(julgamento(p, v))],
      // Julgamentos não se ordenam: o motor embaralha (ver `assemble.ts`).
      raw: (v) => v,
      rng,
    });

    return {
      stem,
      comando: [txt('Essa afirmação se sustenta?')],
      stimuli: [
        {
          kind: 'manipulable',
          motion: 'interactive',
          audio: { need: 'none' },
          id: 'grafico',
          alt: descricao(p.corte),
          controls: [
            {
              id: 'inicio',
              label: [txt('O eixo começa em')],
              min: 0,
              max: p.corte,
              step: PASSO,
              initial: p.corte,
              format: (v) => (v === 0 ? 'zero' : n0(v)),
            },
          ],
          frame: (valores) => {
            const inicio = Math.round(valores['inicio'] ?? p.corte);
            return barras({
              categorias: ctx.categorias,
              valores: [...p.valores],
              yMin: inicio,
              yTop: p.yTop,
              // Com o eixo cortado, a leitura fica apertada: números de 10 em 10 e
              // linhas de 5 em 5, para dar onde apoiar a vista.
              yStep: 10,
              gridStep: 5,
              xLabel: '',
              yLabel: ctx.eixo,
              title: ctx.titulo,
              desc: descricao(inicio),
            });
          },
          // No papel não há controle: vai o gráfico publicado, com o corte à vista.
          quadroImpresso: { inicio: p.corte },
        },
      ],
      interaction,
      dicas: [
        [
          txt(
            'Repare em que número o eixo vertical começa. Leve o controle até zero e olhe as colunas de novo.',
          ),
        ],
        [
          txt(
            `Do zero, ${maior} mede ${n0(val(p, p.iMaior))} e ${menor} mede ${n0(val(p, p.iMenor))}. ` +
              '"Quantas vezes maior" se responde dividindo.',
          ),
        ],
      ],
      rationale: [
        strong('Resolução. '),
        txt(
          `No gráfico publicado o eixo começa em ${n0(p.corte)}. Sobrando só o que passa desse valor, ` +
            `${maior} fica com ${n0(val(p, p.iMaior) - p.corte)} e ${menor} com ` +
            `${n0(val(p, p.iMenor) - p.corte)} — daí as ${n0(p.razaoAparente)} vezes que ${oS(afirmacaoDe(p))} anuncia.`,
        ),
        br,
        txt(
          `Com o eixo a partir do zero, as colunas medem ${n0(val(p, p.iMaior))} e ${n0(val(p, p.iMenor))}: ` +
            `${contaDaRazao(p)}. A diferença é de ` +
            `${n0(diferenca(p))} ${ctx.unidade}, perto de ${n0(Math.round((razaoReal(p) - 1) * 100))}%.`,
        ),
        br,
        txt(
          'Cortar a base do eixo é legítimo quando se quer mostrar uma variação pequena — desde que o corte ' +
            'esteja declarado e que ninguém leia a ALTURA como se fosse a quantidade.',
        ),
      ],
      declared: p.razaoAparente >= 3 ? 'media' : 'facil',
    };
  },

  validate(_b, p) {
    if (p.corte <= 0) return 'o eixo publicado precisa começar acima de zero';
    if (p.valores.some((v) => v <= p.corte))
      return 'há coluna que não aparece no gráfico publicado';
    if (new Set(p.valores).size < 4) return 'duas colunas com o mesmo valor';
    if (Math.max(...p.valores) === val(p, p.iMaior))
      return 'a coluna mais alta é uma das comparadas';
    const r = razaoReal(p);
    // Se a razão verdadeira já for grande, a manchete não é enganosa — e o item perde o sentido.
    if (r > 1.45) return `razão real ${r.toFixed(2)} alta demais para o item`;
    return null;
  },
};
